/**
 * @typedef {Object} Base
 * @property {string} name
 * @property {string} programmaticName
 * @property {string} subName
 * @property {string | undefined} epicAction
 * @property {string | undefined} passiveEffect
 * @property {string | undefined} repeatableEffect
 * @property {number | undefined} repeatableMax
 * @property {number | undefined} timesRepeated
 * @property {Object} img
 * @property {string} img.hs
 * @property {number} number
 * @property {number} hsNumber
 * @property {number} hp
 * @property {Object} set
 * @property {string} set.name
 * @property {string} set.short
 * @property {number} set.number
 * @property {Object} rarity
 * @property {string} rarity.name
 * @property {string} rarity.short
 * @property {string} artist
 * @property {Array<string>} aspect
 * @property {string} color
 *
 */

/**
 * @type {Object.<string, Base>}
 */
export const bases = {
  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * BLUE BASES
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */
  securityComplex: {
    name: "Security Complex",
    programmaticName: "securityComplex",
    epicAction: "Give a Shield token to a non-leader unit.",
    img: {
      hs: "SWH_01_285_Security_Complex_Base_HYP.webp",
    },
    hp: 25,
    aspect: ["vigilance"],
    color: "#2298D2",
  },
  colossus: {
    name: "Colossus",
    programmaticName: "colossus",
    passiveEffect: "Draw one less card in your starting hand.",
    img: {
      hs: "04020283_EN_Colossus_Base_228e3b72af.webp",
    },
    hp: 35,
    aspect: ["vigilance"],
    color: "#2298D2",
  },
  pauCity: {
    name: "Pau City",
    programmaticName: "pauCity",
    passiveEffect: "Each leader unit you control gets +0/+1.",
    img: {
      hs: "0302294_EN_Pau_City_Base_35e40c7c65.webp",
    },
    hp: 26,
    aspect: ["vigilance"],
    color: "#2298D2",
  },
  capitalCity: {
    name: "Vigilance 30",
    programmaticName: "capitalCity",
    img: {
      hs: "SWH_01_286_Capital_City_Base_HYP.webp",
    },
    hp: 30,
    aspect: ["vigilance"],
    color: "#2298D2",
  },
  lofVigilance28: {
    name: "LoF Vigilance force base",
    programmaticName: "lofBlue28",
    passiveEffect:
      "When a friendly Force unit attacks: The Force is with you (create your Force token).",
    img: {
      hs: "05020285_EN_Shadowed_Undercity_Base_4415dad2ce.webp",
    },
    hp: 28,
    aspect: ["vigilance"],
    color: "#2298D2",
  },
  vergenceTemple: {
    name: "Vergence Temple",
    programmaticName: "vengenceTemple",
    passiveEffect:
      "When the regroup phase starts: If you control a unit with 4 or more remaining HP, the Force is with you (create your Force token).",
    img: {
      hs: "05020283_EN_Vergence_Temple_Base_d2ca6afcf2.webp",
    },
    hp: 25,
    aspect: ["vigilance"],
    color: "#2298D2",
  },
  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * GREEN BASES
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */
  energyConversionLab: {
    name: "Energy Conversion Lab",
    programmaticName: "energyConversionLab",
    epicAction:
      "Play a unit that costs 6 resources or less from your hand. Give it AMBUSH for this phase.",
    img: {
      hs: "SWH_01_288_Energy_Conversion_Lab_Base_HYP.webp",
    },
    hp: 25,
    aspect: ["command"],
    color: "#00A44D",
  },
  dataVault: {
    name: "Data vault",
    programmaticName: "dataVault",
    passiveEffect: "Your minimum deck size is increased by 10 cards.",
    img: {
      hs: "04020286_EN_Data_Vault_Base_7305a3e39d.webp",
    },
    hp: 33,
    aspect: ["command"],
    color: "#00A44D",
  },
  droidManufactory: {
    name: "Droid Manufactory",
    programmaticName: "droidManufactory",
    passiveEffect: "When you deploy a leader: Create 2 Battle Droid tokens.",
    img: {
      hs: "0302297_EN_Droid_Manufactory_Base_cd1f4e0cc0.webp",
    },
    hp: 24,
    aspect: ["command"],
    color: "#00A44D",
  },
  commandCenter: {
    name: "Command 30",
    programmaticName: "commandCenter",
    img: {
      hs: "SWH_01_289_command_Center_Base_HYP.webp",
    },
    hp: 30,
    aspect: ["command"],
    color: "#00A44D",
  },
  lofCommand28: {
    name: "LoF command 28",
    programmaticName: "lofCommand28",
    passiveEffect:
      "When a friendly Force unit attacks: The Force is with you (create your Force token).",
    img: {
      hs: "05020287_EN_Jedi_Temple_Base_9423e40d8c.webp",
    },
    hp: 28,
    aspect: ["command"],
    color: "#00A44D",
  },
  mysticMonastary: {
    name: "Mystic Monastary",
    programmaticName: "mysticMonastary",
    repeatableEffect:
      "The Force is with you . Use this ability no more than 3 times each game.",
    repeatableMax: 3,
    timesRepeated: 0,
    img: {
      hs: "05020286_EN_Mystic_Monastery_Base_3ef3dc3db8.webp",
    },
    hp: 25,
    aspect: ["command"],
    color: "#00A44D",
  },

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * RED BASES
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */
  tarkintown: {
    name: "Tarkintown",
    programmaticName: "tarkintown",
    epicAction: "Deal 3 damage to a damaged non-leader unit.",
    img: {
      hs: "SWH_01_291_Tarkintown_Base_HYP.webp",
    },
    hp: 25,
    aspect: ["aggression"],
    color: "#C31924",
  },
  thermalOscillator: {
    name: "Thermal Oscillator",
    programmaticName: "thermalOscillator",
    passiveEffect: "Your minimum deck size is decreased by 5 cards.",
    img: {
      hs: "04020287_EN_Thermal_Oscillator_Base_21d2984f02.webp",
    },
    hp: 27,
    aspect: ["aggression"],
    color: "#C31924",
  },
  shadowCollectiveCamp: {
    name: "Shadow Collective Camp",
    programmaticName: "shadowCollectiveCamp",
    passiveEffect: "When you deploy a leader: Draw a card.",
    img: {
      hs: "0302300_EN_Shadow_Collective_Camp_Base_7221156d80.webp",
    },
    hp: 25,
    aspect: ["aggression"],
    color: "#C31924",
  },
  catacombsOfCadera: {
    name: "Aggression 30",
    programmaticName: "catacombsOfCadera",
    img: {
      hs: "SWH_01_292_Catacombs_Of_Cadera_Base_HYP.webp",
    },
    hp: 30,
    aspect: ["aggression"],
    color: "#C31924",
  },
  lofAggression28: {
    name: "LoF aggression 28",
    programmaticName: "lofAggression28",
    passiveEffect:
      "When a friendly Force unit attacks: The Force is with you (create your Force token).",
    img: {
      hs: "05020290_EN_Fortress_Vader_Base_a94f9de0c9.webp",
    },
    hp: 28,
    aspect: ["aggression"],
    color: "#C31924",
  },
  templeOfDestruction: {
    name: "Temple of Destruction",
    programmaticName: "templeOfDestruction",
    passiveEffect:
      "When a friendly unit deals 3 or more combat damage to an enemy base: The Force is with you .",
    img: {
      hs: "05020289_EN_Temple_of_Destruction_Base_fdc4bdb563.webp",
    },
    hp: 25,
    aspect: ["aggression"],
    color: "#C31924",
  },
  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * YELLOW BASES
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */
  jedhaCity: {
    name: "Jedha City",
    programmaticName: "jedhaCity",
    epicAction: "Give a non-leader unit -4/-0 for this phase.",
    img: {
      hs: "SWH_01_294_Jedha_City_Base_HYP.webp",
    },
    hp: 25,
    aspect: ["cunning"],
    color: "#F0BA32",
  },
  nabatVillage: {
    name: "Nabat Village",
    programmaticName: "nabatVillage",
    passiveEffect:
      "Draw 3 more cards in your starting hand. You can't take a mulligan.\n" +
      "\n" +
      "When the first action phase starts: Put 3 cards from your hand on the bottom of your deck in any order.",
    img: {
      hs: "04020290_EN_Nabat_Village_Base_a9ce26189d.webp",
    },
    hp: 25,
    aspect: ["cunning"],
    color: "#F0BA32",
  },
  administratorsTower: {
    name: "Cunning 30",
    programmaticName: "administratorsTower",
    img: {
      hs: "SWH_01_295_Administrators_Tower_Base_HYP.webp",
    },
    hp: 30,
    aspect: ["cunning"],
    color: "#F0BA32",
  },
  lofCunning28: {
    name: "LoF cunning 28",
    programmaticName: "lofCunning28",
    img: {
      hs: "05020293_EN_Crystal_Caves_Base_1df381e0d5.webp",
    },
    hp: 28,
    aspect: ["cunning"],
    color: "#F0BA32",
  },
  tombOfEilram: {
    name: "Tomb Of Eilram",
    programmaticName: "tombOfEilram",
    repeatableEffect:
      "Action [exhaust a friendly unit]: The Force is with you .",
    repeatableMax: 99,
    img: {
      hs: "05020292_EN_Tomb_of_Eilram_Base_ea9f20002d.webp",
    },
    hp: 25,
    aspect: ["cunning"],
    color: "#F0BA32",
  },
  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * NEUTRAL BASES
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */
  lakeCountry: {
    name: "Lake Country",
    programmaticName: "lakeCountry",
    img: {
      hs: "04020293_EN_Lake_Country_Base_06121aca30.webp",
    },
    hp: 34,
    aspect: ["neutral"],
    color: "#ccc",
  },
};
/**
 * @type {Array<string>}
 */
export const baseKeys = Object.keys(bases);

/**
 * @type {Array<Base>}
 */
export const baseValues = Object.values(bases);

/**
 * @type {Array<string>}
 */
export const baseList = baseValues.map((base) => {
  return `<li class="flex flex-row">
    <span style="background-color: ${base.color}" class="w-[20px] min-h-5 inline-block mr-2">&nbsp;</span>
    <button class="base flex flex-row justify-between w-full p-2" data-base="${base.programmaticName}"><span>${base.name}</span><span>${base.hp}hp</span></button>
    </li>`;
});
